/**
 * PresenceAvatars — reusable component showing avatar bubbles for users
 * currently viewing the same entity (board, page, or card).
 *
 * Polls a viewers endpoint at a configurable interval.
 */

import React, { useEffect, useState, useRef } from 'react';
import * as api from '../services/api-client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Viewer {
  readonly userId: string;
  readonly displayName: string;
  readonly avatarUrl?: string;
}

interface PresenceAvatarsProps {
  /** API endpoint to poll for viewers (e.g., /v1/pages/:id/viewers or /v1/boards/:id/viewers) */
  readonly endpoint: string;
  /** Maximum number of avatars to show before "+N" overflow */
  readonly maxVisible?: number;
  /** Poll interval in milliseconds (default: 15s) */
  readonly pollInterval?: number;
  /** Optional CSS class for the container */
  readonly className?: string;
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

const DEFAULT_POLL_INTERVAL = 15_000;
const DEFAULT_MAX_VISIBLE = 5;

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

function getAvatarColor(userId: string): string {
  const colors = [
    '#2563eb', '#7c3aed', '#db2777', '#dc2626',
    '#ea580c', '#16a34a', '#0891b2', '#4f46e5',
  ];
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = ((hash << 5) - hash + userId.charCodeAt(i)) | 0;
  }
  return colors[Math.abs(hash) % colors.length];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PresenceAvatars({
  endpoint,
  maxVisible = DEFAULT_MAX_VISIBLE,
  pollInterval = DEFAULT_POLL_INTERVAL,
  className = '',
}: PresenceAvatarsProps): React.ReactElement | null {
  const [viewers, setViewers] = useState<Viewer[]>([]);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!endpoint) return;

    const fetchViewers = async () => {
      try {
        const result = await api.get<{ viewers: Viewer[] }>(endpoint);
        setViewers(result.viewers || []);
      } catch {
        // Non-fatal — presence is best-effort
      }
    };

    fetchViewers();
    intervalRef.current = setInterval(fetchViewers, pollInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      setViewers([]);
    };
  }, [endpoint, pollInterval]);

  if (viewers.length === 0) return null;

  return (
    <div className={`flex items-center gap-1 ${className}`} aria-label="Users currently viewing">
      {viewers.slice(0, maxVisible).map((viewer) => (
        <div
          key={viewer.userId}
          className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium text-white shrink-0 ring-2 ring-card"
          style={{ backgroundColor: getAvatarColor(viewer.userId) }}
          title={viewer.displayName}
          aria-label={`${viewer.displayName} is viewing`}
        >
          {viewer.avatarUrl ? (
            <img
              src={viewer.avatarUrl}
              alt={viewer.displayName}
              className="w-full h-full rounded-full object-cover"
            />
          ) : (
            getInitials(viewer.displayName)
          )}
        </div>
      ))}
      {viewers.length > maxVisible && (
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium bg-muted text-muted-foreground shrink-0 ring-2 ring-card"
          title={`${viewers.length - maxVisible} more`}
        >
          +{viewers.length - maxVisible}
        </div>
      )}
    </div>
  );
}
